import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../redux/actions/auth";
import Steps from "../../../components/Common/Steps";
import ProgressBar from "../../../components/ProgessBar";
import { Currency } from "../../../components/DraftClient";
import SumsubKYCCheck from "../../../components/DraftClient/SumsubKYCCheck";
import { saveDraftCurrencies } from "../../../redux/actions/register/draftClient";
import { updateUserStatus } from "../../../redux/actions/register/updateUserStatus";
import LoadingModal from "../../../components/Modals/LoadingModal";
import { SmallButton } from "../../../components/Buttons";
import { useLocation, useNavigate } from "react-router-dom";
import { setPageTitle } from "../../../helper/metaDataHelper";
import ErrorMessage from "../../../components/Common/ErrorMessage";
import { registerDraftClient } from "../../../redux/actions/register/draftClient";
import { GetValidationErrors } from "../../../helper";
import CreateSubAccount from "../../../components/DraftClient/CreateSubAccount";
import { useModules } from "../../../hooks";

const getSavedState = () => {
  try {
    const saved = sessionStorage.getItem("subAccountFlowSumSub");
    return saved ? JSON.parse(saved) : null;
  } catch (e) {
    return null;
  }
};

const DraftClientSumSub = () => {
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isSumSubEnabled = useModules("Sumsub");

  const rawSavedState = getSavedState();

  const isSubAccount =
    location.state?.isSubAccount === true ||
    rawSavedState?.isSubAccount === true;
  const parentClientId =
    location.state?.parentClientId || rawSavedState?.parentClientId;
  const userid = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo")).id
    : null;

  const getEffectiveState = () => {
    if (!rawSavedState) return null;
    if (
      rawSavedState.parentClientId &&
      parentClientId &&
      rawSavedState.parentClientId !== parentClientId
    ) {
      return null;
    }
    return rawSavedState;
  };

  const savedState = getEffectiveState();

  useEffect(() => {
    if (rawSavedState && !savedState) {
      sessionStorage.removeItem("subAccountFlowSumSub");
    }
  }, [rawSavedState, savedState]);

  const [subAccountClientId, setSubAccountClientId] = useState(
    savedState?.subAccountClientId || location.state?.sub_client_id || null,
  );
  const [subAccountUserId, setSubAccountUserId] = useState(
    savedState?.subAccountUserId || location.state?.sub_user_id || null,
  );

  // Start on step 2 if we are creating a sub account and already have a sub_client_id
  const initialStep =
    savedState?.step || (isSubAccount && location.state?.sub_client_id ? 2 : 1);
  const [step, setStep] = useState(initialStep);

  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccess, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState(() => {
    const baseData = {
      currency_ids: [],
      account_type:
        user?.account_type || location.state?.account_type || "individual",
      name: "",
      email_address: "",
    };
    if (savedState?.formData) {
      return { ...baseData, ...savedState.formData };
    }
    return baseData;
  });

  const activeAccountType = isSubAccount
    ? formData.account_type || "individual"
    : user.account_type;

  const labels = isSubAccount
    ? ["Account Details", "Currency", "Identity verification"]
    : ["Currency", "Identity verification"];

  useEffect(() => {
    setPageTitle(
      `${activeAccountType.charAt(0).toUpperCase() + activeAccountType.slice(1)} Client Onboarding`,
    );
  }, [activeAccountType]);

  // Persist State to sessionStorage for Sub Accounts (ONLY Currency & Account Type)
  useEffect(() => {
    if (isSubAccount && !isSuccess) {
      const stateToSave = {
        isSubAccount,
        parentClientId,
        subAccountClientId,
        subAccountUserId,
        step,
        formData: {
          currency_ids: formData.currency_ids,
          account_type: formData.account_type,
        },
      };
      sessionStorage.setItem(
        "subAccountFlowSumSub",
        JSON.stringify(stateToSave),
      );
    }
  }, [
    formData.currency_ids,
    formData.account_type,
    step,
    isSubAccount,
    parentClientId,
    subAccountClientId,
    subAccountUserId,
    isSuccess,
  ]);

  const onChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files.length > 0) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: files,
      }));
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }
  };

  const handleNext = () => {
    if (step < labels.length) setStep(step + 1);
  };

  const handlePrev = () => {
    if (isSubAccount && step === 2) {
      // If we are on Currency (Step 2) for Sub Accounts, don't allow going back to Account Details
      return;
    }
    if (step > 1) setStep(step - 1);
  };

  const handleLogout = async () => {
    sessionStorage.removeItem("subAccountFlowSumSub");
    setIsLoading(true);
    setIsModalOpen(true);
    await dispatch(logout());
    setIsLoading(false);
    setIsModalOpen(false);
  };

  const handleCreateSubAccountDraft = async () => {
    setError("");
    setIsLoading(true);

    const draftData = {
      name: formData.name,
      email_address: formData.email_address,
      account_type: formData.account_type,
      parent_client_id: parentClientId,
      user_id: userid,
    };

    try {
      const response = await dispatch(registerDraftClient(draftData));

      if (response && response.success) {
        setSubAccountClientId(response.sub_client_id);
        setSubAccountUserId(response.user_id);
        setIsLoading(false);
        handleNext();
      } else {
        setIsLoading(false);
        if (response?.errors) {
          const errorMessages = GetValidationErrors(response.errors);
          setError(errorMessages.flat().join(", "));
        } else {
          setError(response?.message || "Failed to create sub-account");
        }
      }
    } catch (e) {
      setIsLoading(false);
      setError("An unexpected network error occurred.");
    }
  };

  const handleCancleSubAccount = () => {
    sessionStorage.removeItem("subAccountFlowSumSub");
    navigate("/");
  };

  const handleLiveness = async () => {
    if (formData.currency_ids.length === 0) {
      setError("Please select at least one currency.");
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      const payload = {
        currency_ids: formData.currency_ids.join(","),
      };

      if (isSubAccount && subAccountClientId) {
        payload.sub_client_id = subAccountClientId;
      }

      const response = await dispatch(saveDraftCurrencies(payload));

      if (response && response.success) {
        setIsLoading(false);
        handleNext();
      } else {
        setIsLoading(false);
        setError(response?.message);
      }
    } catch (e) {
      setIsLoading(false);
      setError("An unexpected network error occurred.");
      return;
    }
  };

  const handleFinalSubmission = async () => {
    setIsLoading(true);
    setIsModalOpen(true);
    setError("");

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));

      const response = { success: true };

      if (response.success) {
        sessionStorage.removeItem("subAccountFlowSumSub");
        setSuccess(true);
        setTimeout(() => {
          setIsModalOpen(false);
          setSuccess(false);
          if (isSubAccount) {
            navigate("/sub-account");
          } else {
            dispatch(updateUserStatus("Pending"));
            navigate("/");
          }
        }, 2000);
      } else {
        setIsLoading(false);
        setError("Server error. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setError("An unexpected error occurred.");
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    const currentLabel = labels[step - 1];

    switch (currentLabel) {
      case "Account Details":
        return (
          <CreateSubAccount
            formData={formData}
            onChange={onChange}
            handleCreateDraft={handleCreateSubAccountDraft}
            handleCancle={handleCancleSubAccount}
            isLoading={isLoading}
            isSumSubEnabled={isSumSubEnabled}
          />
        );
      case "Currency":
        return (
          <Currency
            handleNext={handleLiveness}
            handlePrev={handlePrev}
            formData={formData}
            setFormData={setFormData}
            isSubAccount={isSubAccount}
            handleLiveness={handleLiveness}
            hideBackButton={true}
            isLoading={isLoading}
          />
        );
      case "Identity verification":
        return (
          <SumsubKYCCheck
            handleNext={handleFinalSubmission}
            handlePrev={handlePrev}
            isSubAccount={isSubAccount}
            subAccountClientId={subAccountClientId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="md:max-w-[992px] mx-auto min-h-dvh flex-1 flex flex-col">
        <div className="inline-flex space-x-2 px-2 pb-8">
          <SmallButton onClick={handleLogout} text={"Logout"} cancel={true} />
        </div>
        <div className="flex flex-col md:items-center md:justify-center">
          <Steps step={step} labels={labels} />
          <ProgressBar step={step} labels={labels} />
        </div>

        <div className="flex flex-col md:items-center w-full px-3 justify-center md:p-6">
          {error && (
            <ErrorMessage message={error} onClose={() => setError("")} />
          )}
          <div className="py-4">{renderStepContent()}</div>
        </div>
      </div>
      <LoadingModal
        isLoading={isLoading}
        setisLoading={setIsLoading}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        isSuccess={isSuccess}
        setSuccess={setSuccess}
        title={"Setup Complete"}
      />
    </>
  );
};

export default DraftClientSumSub;
